import { Injectable } from '@angular/core';
import { BaseSmartFilterFormViewModel } from '../base-smart-filter-form-view-model';
import { ProductType, ProductTypeDefinition } from '../../../../../utils/product-type-definition';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { delay, distinctUntilChanged, map, pairwise, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CompanyDomainModel } from '../../../../../domainModels/company-domain-model';
import { ProductGroupDomainModel } from '../../../../../domainModels/product-group-domain-model';
import { ArrayUtils } from '../../../../../utils/array-utils';

@Injectable()
export class ProductAddEditSmartFilterFormViewModel extends BaseSmartFilterFormViewModel {
  constructor(
    private productGroupDomainModel: ProductGroupDomainModel,
    private companyDomainModel: CompanyDomainModel
  ) {
    super();
  }

  private readonly MIN_PRICE = 0.01;
  private readonly MIN_SECONDARY_PRICE = 0.01;
  private readonly MIN_UNIT_SIZE = 0.01;
  private readonly MIN_INVENTORY = 1;
  private readonly MIN_PACKAGE_QUANTITY = 1;

  public dropdownProductTypes$ = window?.types?.productTypes$ || of<ProductTypeDefinition[]>([]);

  private _selectedProductType = new BehaviorSubject<ProductType>(undefined);
  public selectedProductType$ = this._selectedProductType as Observable<ProductType>;
  public connectToSelectedProductType = (pt: ProductType) => this._selectedProductType.next(pt);

  public readonly variantTypesCommaDelimitedString$ = this.selectedProductType$.pipe(
    switchMap(productType => window?.types?.getVariantTypesForProductType$(productType)),
    map(types => types.map(t => t.value).join(',')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly resetVariantTypes$ = this.selectedProductType$.pipe(
    distinctUntilChanged(),
    pairwise(),
    switchMap(([previous, current]) => {
      if (!previous || previous === current) return of(undefined);
      return of(undefined).pipe(delay(100), startWith(null));
    }),
    startWith(undefined),
    distinctUntilChanged()
  );

  public readonly variantTypesEnabled$ = combineLatest([this.variantTypesCommaDelimitedString$, this.isViewOnly$]).pipe(
    map(([vts, isViewOnly]) => vts?.length > 0 && !isViewOnly)
  );

  public readonly strainTypesCommaDelimitedString$ = window?.types?.strainTypesWithoutCBDStrain$.pipe(
    map(types => types.map(t => t.value).join(','))
  );

  private _minPrice = new BehaviorSubject<number>(this.MIN_PRICE);
  public minPrice$ = this._minPrice as Observable<number>;
  connectToMinPrice = (minPrice: number) => this._minPrice.next(this.getMinimumValue(minPrice, this.MIN_PRICE));

  private _maxPrice = new BehaviorSubject<number>(undefined);
  public maxPrice$ = this._maxPrice as Observable<number>;
  connectToMaxPrice = (maxPrice: number) => this._maxPrice.next(maxPrice);

  private _minPackagedQuantity = new BehaviorSubject<number>(this.MIN_PACKAGE_QUANTITY);
  public minPackagedQuantity$ = this._minPackagedQuantity as Observable<number>;
  public connectToMinPackagedQuantity = (minPackagedQuantity: number) => {
    this._minPackagedQuantity.next(this.getMinimumValue(minPackagedQuantity, this.MIN_PACKAGE_QUANTITY));
  };

  private _maxPackagedQuantity = new BehaviorSubject<number>(undefined);
  public maxPackagedQuantity$ = this._maxPackagedQuantity as Observable<number>;
  public connectToMaxPackagedQuantity = (maxPackagedQuantity: number) => {
    this._maxPackagedQuantity.next(maxPackagedQuantity);
  };

  private _useRangedPackageQuantities = new BehaviorSubject<boolean>(false);
  public useRangedPackageQuantities$ = this._useRangedPackageQuantities as Observable<boolean>;
  public connectToUseRangedPackageQuantities = (useRangedPackageQuantities: boolean) => {
    this._useRangedPackageQuantities.next(useRangedPackageQuantities);
  };

  private _minUnitSize = new BehaviorSubject<number>(this.MIN_UNIT_SIZE);
  public minUnitSize$ = this._minUnitSize as Observable<number>;
  public connectToMinUnitSize = (minUnitSize: number) => {
    this._minUnitSize.next(this.getMinimumValue(minUnitSize, this.MIN_UNIT_SIZE));
  };

  private _maxUnitSize = new BehaviorSubject<number>(undefined);
  public maxUnitSize$ = this._maxUnitSize as Observable<number>;
  public connectToMaxUnitSize = (maxUnitSize: number) => this._maxUnitSize.next(maxUnitSize);

  private _useRangedUnitSizes = new BehaviorSubject<boolean>(false);
  public useRangedUnitSizes$ = this._useRangedUnitSizes as Observable<boolean>;
  public connectToUseRangedUnitSizes = (useRangedUnitSizes: boolean) => {
    this._useRangedUnitSizes.next(useRangedUnitSizes);
  };

  private _minSecondaryPrice = new BehaviorSubject<number>(this.MIN_SECONDARY_PRICE);
  public minSecondaryPrice$ = this._minSecondaryPrice as Observable<number>;
  connectToMinSecondaryPrice = (minSecondaryPrice: number) => {
    this._minSecondaryPrice.next(this.getMinimumValue(minSecondaryPrice, this.MIN_SECONDARY_PRICE));
  };

  private _maxSecondaryPrice = new BehaviorSubject<number>(undefined);
  public maxSecondaryPrice$ = this._maxSecondaryPrice as Observable<number>;
  connectToMaxSecondaryPrice = (maxSecondaryPrice: number) => this._maxSecondaryPrice.next(maxSecondaryPrice);

  private _minInventory = new BehaviorSubject<number>(this.MIN_INVENTORY);
  public minInventory$ = this._minInventory as Observable<number>;
  public setMinInventory = (i: number) => this._minInventory.next(this.getMinimumValue(i, this.MIN_INVENTORY));

  private _maxInventory = new BehaviorSubject<number>(undefined);
  public maxInventory$ = this._maxInventory as Observable<number>;
  public setMaxInventory = (i: number) => this._maxInventory.next(i);

  public readonly universalVariantBrandsDelimited$ = this.productGroupDomainModel.universalVariantBrandsDelimited$;

  public readonly strainsDelimited$ = this.productGroupDomainModel.universalVariants$.pipe(
    map(variants => ArrayUtils.uniqueNonEmptyStrings(variants?.map(v => v?.strain?.trim()).filterFalsies())),
    map(strains => strains?.join(',')),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * only lowercase when using for smart filters, since the saved SF value is lowercased
   */
  public readonly lineagesDelimited$ = this.productGroupDomainModel.variantLineages$.pipe(
    map(lineages => lineages?.map(l => l?.toLowerCase().trim())?.join(','))
  );

  private getMinimumValue(input: number, smallestMinValue: number): number {
    if (!Number.isFinite(input) || input < smallestMinValue) {
      return smallestMinValue;
    }
    return input;
  }
}
